import {
  enabledSizes,
  ingredientForSize,
  type PizzaConfig,
  type PizzaIngredientLike,
  type PizzaSize,
} from './pizza-config'
import { formatCents, parseMoneyToCents } from './money'
import { tt } from './i18n/translation-memory'

/**
 * Moved here from `manager-orders` on 2026-08-03 so the customer app can build a pizza too — §5 says
 * anything two modules need lives in `packages/*`, and FRONTEND.md §9.19 is the entry that needed it.
 * The logic is unchanged; only its address is.
 */

/**
 * Building a pizza on `/new-order` — the §14.6 configurator's staff-facing half.
 *
 * The config is already in `@agos/shared/pizza-config` with 22 tests and `/new-order` did not use it, so
 * a pizza could be added at its base price and not customised. Measured: two sizes (32 cm at 5,20 €,
 * 50 cm at 11,00 €), two bases (rajčinový and smotanový, 0,30 € and 1,00 € at both sizes), 38 ingredients
 * each carrying its own per-size price, and `garlicCrustEnabled: true` at `garlicCrustPrice: 0`.
 *
 * **Every price is per size and none of them scales.** `ingredientForSize` picks the exact size's values
 * and falls back to the nearest larger one; a 50 cm mozzarella is its own number, not a 32 cm one doubled.
 * So the total is the size's `basePrice`, plus the chosen base at that size, plus each ingredient at that
 * size, plus the garlic crust if it is taken — and nothing here multiplies anything by a ratio.
 *
 * **The crust is an ingredient, not a term here — §9.93.** An `Okraj` row is chosen and priced like any
 * other topping, which is why nothing below adds a crust to a total: it is already in `toppings`.
 * `PizzaConfig.garlicCrustEnabled` and `garlicCrustPrice` survive because the endpoint still carries
 * them, and are read by nothing.
 *
 * The line is written the way the customer app writes one — a name carrying the size and the choices —
 * because the kitchen board, the receipt and `/restaurant`'s bill all read that name and none of them
 * knows about this module.
 */

/**
 * An ingredient as this screen needs it.
 *
 * `PizzaIngredientLike` in `@agos/shared` is only the *pricing* shape — it carries `price32`, `sizeData`
 * and the rest, and deliberately no identity, because the costing helpers do not need one. A picker does.
 */
export interface BuilderIngredient extends PizzaIngredientLike {
  id: number
  name: string
  /**
   * The category row this ingredient sits in — read by {@link ingredientGroups}, and by §9.61 to
   * find the crust. Optional because a caller that only prices an ingredient does not need it.
   */
  category?: string | null
  /** Both spellings reach the builder: the entity says `active`, some payloads say `isActive`. */
  active?: boolean
  isActive?: boolean
}

export interface PizzaDraft {
  sizeCm: number | null
  baseValue: string | null
  /**
   * The toppings, in the order they were chosen — §9.43.
   *
   * **An id twice is a double portion.** One value rather than a set beside a `doubled` list: two
   * structures that have to agree are the shape §9.7 warns about, and keeping them agreeing would be
   * left to every handler. `pizzaPriceCents` sums per entry, so twice costs twice with no arithmetic
   * added, and `ingredientQty` reads it back for a screen.
   */
  ingredientIds: number[]
}

export const EMPTY_PIZZA: PizzaDraft = {
  sizeCm: null,
  baseValue: null,
  ingredientIds: [],
}

export function sizesOf(config: PizzaConfig | null): PizzaSize[] {
  return enabledSizes(config)
}

export function sizeOf(draft: PizzaDraft, config: PizzaConfig | null): PizzaSize | null {
  if (draft.sizeCm === null) return null
  return sizesOf(config).find((size) => size.cm === draft.sizeCm) ?? null
}

/** A base's price at this size — the per-size map, with the legacy single `price` as the fallback. */
export function basePriceCents(
  baseValue: string | null,
  config: PizzaConfig | null,
  sizeCm: number | null,
): number {
  if (baseValue === null || sizeCm === null) return 0
  const base = (config?.bases ?? []).find((candidate) => candidate.value === baseValue)
  if (base === undefined) return 0
  const perSize = (base as { prices?: Record<string, unknown> }).prices
  const atSize = perSize?.[String(sizeCm)]
  if (atSize !== undefined) return parseMoneyToCents(atSize as string | number)
  return parseMoneyToCents((base as { price?: unknown }).price as string | number | null)
}

export function ingredientPriceCents(ingredient: PizzaIngredientLike, sizeCm: number): number {
  return parseMoneyToCents(ingredientForSize(ingredient, sizeCm).price)
}

export function chosenIngredients(
  draft: PizzaDraft,
  ingredients: readonly BuilderIngredient[],
): BuilderIngredient[] {
  // In the order they were chosen, not catalogue order — the description reads as it was built.
  return draft.ingredientIds.flatMap((id) => {
    const found = ingredients.find((candidate) => candidate.id === id)
    return found === undefined ? [] : [found]
  })
}

/** How many portions of one topping this draft carries: 0, 1 or 2. */
export function ingredientQty(draft: PizzaDraft, id: number): number {
  return draft.ingredientIds.filter((candidate) => candidate === id).length
}

/** Each chosen topping once, with how many portions — for a name or a tile, never for the price. */
export function distinctChosen(
  draft: PizzaDraft,
  ingredients: readonly BuilderIngredient[],
): { ingredient: BuilderIngredient; qty: number }[] {
  const seen = new Set<number>()
  return chosenIngredients(draft, ingredients).flatMap((ingredient) => {
    if (seen.has(ingredient.id)) return []
    seen.add(ingredient.id)
    return [{ ingredient, qty: ingredientQty(draft, ingredient.id) }]
  })
}

/**
 * One tap adds, a second doubles, a third removes — §9.43, and §9.41's cycle for the same reason.
 *
 * The second entry goes in beside the first rather than at the end, so a double portion keeps the
 * position it was chosen at and the line reads the way it was built.
 */
export function toggleIngredient(draft: PizzaDraft, id: number): PizzaDraft {
  const qty = ingredientQty(draft, id)
  if (qty === 0) return { ...draft, ingredientIds: [...draft.ingredientIds, id] }
  if (qty >= 2) {
    return { ...draft, ingredientIds: draft.ingredientIds.filter((candidate) => candidate !== id) }
  }
  const doubled: number[] = []
  for (const candidate of draft.ingredientIds) {
    doubled.push(candidate)
    if (candidate === id) doubled.push(id)
  }
  return { ...draft, ingredientIds: doubled }
}

/** The whole pizza, in cents. A plain integer sum — every part is already cents (FB-056). */
export function pizzaPriceCents(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): number {
  const size = sizeOf(draft, config)
  if (size === null) return 0
  const base = parseMoneyToCents(size.basePrice)
  const sauce = basePriceCents(draft.baseValue, config, size.cm)
  const toppings = chosenIngredients(draft, ingredients).reduce(
    (total, ingredient) => total + ingredientPriceCents(ingredient, size.cm),
    0,
  )
  // §9.93 — no crust term. An `Okraj` row is in `toppings` above, at its own per-size price; adding
  // it here as well billed a garlic crust twice, once as an ingredient and once as a crust.
  return base + sauce + toppings
}

/** What stops the pizza being added. */
export function pizzaProblems(draft: PizzaDraft, config: PizzaConfig | null): string[] {
  const found: string[] = []
  if (sizeOf(draft, config) === null) found.push(tt('Vyberte veľkosť'))
  if (draft.baseValue === null) found.push(tt('Vyberte základ'))
  return found
}

/**
 * The line's name, in the shape everything downstream already reads.
 *
 * `Vlastná pizza – 32 cm (Rajčinový základ, Šunka, Syr, cesnakový okraj)`. The kitchen board, the
 * receipt and the bill split all read this string; none of them knows this module exists, so the shape
 * is the contract.
 */
export function pizzaLineName(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): string {
  const size = sizeOf(draft, config)
  const base = (config?.bases ?? []).find((candidate) => candidate.value === draft.baseValue)
  const parts = [
    ...(base === undefined ? [] : [base.label]),
    // §9.43 — `Šunka 2×`, not `Šunka, Šunka`. The same spelling §9.41 puts on a menu pizza's
    // toppings, because the kitchen reads one string for both.
    ...distinctChosen(draft, ingredients).map(({ ingredient, qty }) =>
      qty === 2 ? `${ingredient.name} 2×` : ingredient.name,
    ),
    // §9.73 — the crust's own name. With one crust this still reads *cesnakový okraj*; with two it
    // is the difference between a ticket the kitchen can act on and one that says a crust was wanted.
    // §9.93 — no crust here either: it is one of the toppings above, named in the order it was
    // chosen. Appending it again wrote it onto the line twice.
  ]
  const inside = parts.length === 0 ? '' : ` (${parts.join(', ')})`
  return `${tt('Vlastná pizza')} – ${size === null ? '' : `${size.cm} cm`}${inside}`.replace('–  ', '– ')
}

export function pizzaSummary(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): string {
  if (sizeOf(draft, config) === null) return tt('Vyberte veľkosť a základ.')
  return `${pizzaLineName(draft, config, ingredients)} - ${formatCents(pizzaPriceCents(draft, config, ingredients))}`
}

/**
 * A custom pizza's cart id.
 *
 * Negative, like a lunch dish, because it is not a catalogue product — and distinct per configuration, so
 * two differently-built pizzas are two lines rather than one of quantity two.
 */
export function pizzaCartId(name: string): number {
  let hash = 5381
  for (let index = 0; index < name.length; index += 1) {
    hash = (Math.imul(hash, 33) ^ name.charCodeAt(index)) | 0
  }
  return hash >= 0 ? -(hash + 1) : hash
}

/**
 * The category legacy keeps out of the toppings, because it *is* the pizza's base.
 *
 * `pizza-builder-modal.tsx:77` reads it as the base selector — `i.category === "Základ"` — and its
 * `CATS` list never mentions it, so it cannot also appear as something to put on top. AGOS took its
 * bases from `/settings/pizza-config` instead, which answers the same two rows, and rendered the
 * whole ingredient table underneath: *Rajčinový základ* and *Smotanový základ* appeared twice on
 * the screen and could be charged for as toppings on a pizza that already had one.
 *
 * Raw Slovak, because it is a value stored in `pizza_ingredients.category` rather than anything a
 * reader sees — the heading is translated where it is drawn.
 */
export const PIZZA_BASE_CATEGORY = 'Základ'

/**
 * The category a crust lives in — `FRONTEND.md` §9.61, owner decision third round.
 *
 * Raw Slovak for the same reason {@link PIZZA_BASE_CATEGORY} is: it is a value stored in
 * `pizza_ingredients.category`, not a label anybody reads.
 */
export const PIZZA_CRUST_CATEGORY = 'Okraj'

/**
 * The category order: crust first, then legacy's cheese, meat, vegetables.
 *
 * Legacy's `CATS` is the last three and they are kept in its order — a habit worth not disturbing,
 * and re-sorting it alphabetically would reshuffle a screen nobody asked to have reshuffled.
 *
 * **`Základ` is first by §9.237, `Okraj` second by §9.179.** The base is chosen before the crust and
 * the crust before the cheese; legacy lists the base first of its four. Before §9.235 there was no
 * base group to rank — the customer builder still has none, because it passes `includeBase: false`.
 *
 * **§9.179's argument, applied once more.** §9.93 made the crust an ordinary topping rather than a special
 * control, and an ordinary topping outside this list ranks *after* all of it — so the first thing
 * anybody chooses about a pizza was sorted to the bottom. One list orders four screens: both
 * builders, the customer's toppings prompt and the manager's extras dialog.
 */
const KNOWN_CATEGORY_ORDER = [PIZZA_BASE_CATEGORY, 'Okraj', 'Syr', 'Mäso', 'Zelenina']

/** Where an ingredient with no category at all is put, rather than dropped. */
const UNCATEGORISED = 'Ostatné'

export interface IngredientGroup {
  /** The stored Slovak category, or `Ostatné`. Translate at the point it is drawn. */
  category: string
  ingredients: BuilderIngredient[]
}

/**
 * The toppings a person may choose, grouped under the category they were filed in.
 *
 * The owner's report is *"treba ich zoradiť podľa kategórií, ktoré si človek zvolí, toto je zle"*,
 * and both AGOS surfaces rendered one flat list in whatever order the endpoint answered.
 *
 * **The categories come from the data.** Legacy hard-codes three and filters to them, which means an
 * ingredient in a category a franchise adds later is not sorted last — it is not rendered at all, and
 * is unsellable with nothing on screen to say so. The three keep their order; anything else the
 * franchise has made follows alphabetically under its own heading, so adding a category adds a
 * section rather than losing an ingredient.
 *
 * **`active`, and `isActive` too.** The wire field is `active`. The customer app filtered on
 * `isActive`, which is on no row, so its guard has never removed anything — read both, and a surface
 * that switches an ingredient off gets what it asked for either way.
 */
export function ingredientGroups(
  ingredients: readonly BuilderIngredient[] | undefined,
  /**
   * Whether the **Základ** category is offered — §9.235.
   *
   * Off by default, which keeps the customer builder exactly as §9.93 left it: a customer chooses a
   * base separately and offering it again would charge for a second. The manager's product dialog
   * passes `true`, because there the chips describe what the product **is made of** and the base is
   * its first ingredient — *"I want to manually select which základ we will use"*.
   */
  includeBase = false,
): IngredientGroup[] {
  const groups = new Map<string, BuilderIngredient[]>()

  for (const ingredient of ingredients ?? []) {
    const row = ingredient as BuilderIngredient & {
      category?: unknown
      active?: unknown
      isActive?: unknown
      sortOrder?: unknown
    }
    if (row.active === false || row.isActive === false) continue

    const category = typeof row.category === 'string' ? row.category.trim() : ''
    // §9.93 — only the base is excluded, because the pizza already has one and offering it as a
    // topping would charge for a second. An `Okraj` row is an ordinary ingredient now: the owner
    // asked for *"the same logic as other ingrediencies… you can select both of them"*, and that is
    // what an ingredient already does.
    if (!includeBase && category === PIZZA_BASE_CATEGORY) continue

    const key = category === '' ? UNCATEGORISED : category
    const bucket = groups.get(key)
    if (bucket) bucket.push(ingredient)
    else groups.set(key, [ingredient])
  }

  const rank = (category: string): number => {
    const known = KNOWN_CATEGORY_ORDER.indexOf(category)
    if (known !== -1) return known
    // Anything the franchise made goes after the three, and the uncategorised after that.
    return category === UNCATEGORISED ? Number.MAX_SAFE_INTEGER : KNOWN_CATEGORY_ORDER.length
  }

  return [...groups.entries()]
    .sort(([left], [right]) => {
      const byRank = rank(left) - rank(right)
      return byRank !== 0 ? byRank : left.localeCompare(right, 'sk')
    })
    .map(([category, rows]) => ({
      category,
      // `sortOrder` is what the ingredient editor writes when somebody drags a row, and it is spaced
      // in tens — so a tie is two rows nobody has ordered, and the name decides rather than the
      // order the database happened to answer in.
      ingredients: [...rows].sort((left, right) => {
        const leftOrder = Number((left as { sortOrder?: unknown }).sortOrder ?? 0)
        const rightOrder = Number((right as { sortOrder?: unknown }).sortOrder ?? 0)
        const byOrder =
          (Number.isFinite(leftOrder) ? leftOrder : 0) - (Number.isFinite(rightOrder) ? rightOrder : 0)
        return byOrder !== 0 ? byOrder : left.name.localeCompare(right.name, 'sk')
      }),
    }))
}

/** One part of a composed line, as `POST /orders` takes it — `BACKEND.md §10A.14`. */
export interface PizzaPart {
  productId?: number
  name: string
  unitPriceCents: number
  quantity: number
}

/**
 * What a custom pizza is assembled from, for the server to cap the line against — §10A.14.
 *
 * **The sum of these is `pizzaPriceCents` exactly**, and deliberately so: the parts are the same four
 * terms that function adds, read the same way. A cap that did not agree with the price would either
 * refuse honest baskets or leave the hole `BLOCKED.md` #12 is about.
 *
 * **Almost none of them has a catalogue row.** A size, a base and a topping are not products, so they
 * are sent at the price the builder charged and the server takes that figure. That is not a hole: it
 * is the same figure the line itself would have carried, and the cap still stops a line claiming more
 * than the parts it declares. Only a part naming a real product can be re-priced, and a custom pizza
 * has none.
 *
 * A double portion is **one part with `quantity: 2`** — §9.43's multiset read back — because two
 * entries for the same topping would be a different claim about what the kitchen makes.
 */
export function pizzaParts(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): PizzaPart[] {
  const size = sizeOf(draft, config)
  if (size === null) return []

  const parts: PizzaPart[] = [
    { name: size.label, unitPriceCents: parseMoneyToCents(size.basePrice), quantity: 1 },
  ]

  const base = (config?.bases ?? []).find((candidate) => candidate.value === draft.baseValue)
  if (base !== undefined) {
    parts.push({
      name: base.label,
      unitPriceCents: basePriceCents(draft.baseValue, config, size.cm),
      quantity: 1,
    })
  }

  for (const { ingredient, qty } of distinctChosen(draft, ingredients)) {
    parts.push({
      name: ingredient.name,
      unitPriceCents: ingredientPriceCents(ingredient, size.cm),
      quantity: qty,
    })
  }

  // §9.93 — no crust part. §9.61's rule stands and is why this had to go with the other two: the
  // parts must sum to the price, and the price no longer carries a crust term because an `Okraj` row
  // is one of the toppings emitted above.
  return parts
}

/** Legacy's cheese, matched on a substring of the name. Its own rule, and there is no id to read. */
const DEFAULT_CHEESE = 'mozzarella'

/**
 * A fresh pizza for this franchise — `FRONTEND.md` §9.60, §9.188.
 *
 * `EMPTY_PIZZA` is the blank one and stays blank; this is the one a builder should open with.
 *
 * **A franchise's `defaultBaseValue` wins, and the first base is the fallback — §9.188.** This
 * paragraph used to argue the opposite: *"`bases[0]` in particular is the wrong fallback: the array's
 * order is the order somebody typed them in, so it would move the preselection whenever a base is
 * added or reordered."* That is true and it is the smaller problem. `defaultBaseValue` is **empty on
 * the owner's data**, so the honest consequence of refusing a fallback was a builder that preselected
 * nothing for every customer — while legacy takes `pizzaBases[0].id` and always has.
 *
 * **An unrecognised default still preselects nothing of its own** and falls to the first base rather
 * than to a substitute the franchise did choose but which no longer exists.
 *
 * **The cheese is legacy's `name.includes('mozzarella')`**, one portion. Fragile, and the alternative
 * here is nothing: there is no `defaultIngredientIds` in the config to read. Only if it is offered —
 * a franchise without mozzarella gets no cheese, not the first ingredient in the list.
 *
 * **One portion and not two**, because the cycle is 0 → 1 → 2: a preselected double would make the
 * customer's first tap *remove* half of something they never asked for.
 */
export function newPizza(
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[] = [],
): PizzaDraft {
  const bases = config?.bases ?? []
  const wanted = (config?.defaultBaseValue ?? '').trim()
  const chosen = bases.find((base) => base.value === wanted)?.value ?? bases[0]?.value ?? null

  const cheese = ingredients.find(
    (ingredient) =>
      ingredient.name.toLowerCase().includes(DEFAULT_CHEESE) &&
      (ingredient as { active?: boolean; isActive?: boolean }).active !== false &&
      (ingredient as { active?: boolean; isActive?: boolean }).isActive !== false,
  )

  return {
    ...EMPTY_PIZZA,
    baseValue: chosen,
    ingredientIds: cheese === undefined ? [] : [cheese.id],
  }
}
